import { createReducer, on } from '@ngrx/store';
import { Project } from '../../features/projects/interfaces/project.interface';
import {
  ActiveDetails,
  Career,
  CareerHeroSection,
  Contact,
  Expertise,
  ExpertiseHeroSection,
  HomeHeroSection,
  News,
  Story,
  User,
} from '../../shared/interfaces/shared-interface';
import * as GlobalActions from './global.actions';

export interface GlobalState {
  profile: User | any[];
  emailLoading: boolean;
  translations: any;
  projectsList: Project[];
  expertisesList: Expertise[];
  expertiseHeroSection: ExpertiseHeroSection | null;
  expertisePageTitle: string | null;
  expertisePageDescription: string | null;
  newsList: News[];
  contactsList: Contact[];
  activeDetails: ActiveDetails;
  careers: Career[];
  careerHeroSection: CareerHeroSection | null;
  homeHeroSection: any | null;
  isLoading: boolean;
  isLoadingProjectMediaDelete: boolean;
  video: Blob | null;
  story: Story | any;
  policy: any;
  accreditation: any;
  leadership: any;
  error: Error | null;
}

const initialState: GlobalState = {
  activeDetails: {
    locationList: [],
    typeList: [],
    expertiseList: [],
    categoryList: [],
    departmentList: [],
    careerStatusList: [],
    videoHome: null,
  },
  video: null,
  newsList: [],
  contactsList: [],
  profile: [],
  emailLoading: false,
  translations: {},
  projectsList: [],
  expertisesList: [],
  expertiseHeroSection: null,
  expertisePageTitle: null,
  expertisePageDescription: null,
  careers: [],
  careerHeroSection: null,
  homeHeroSection: null,
  isLoading: false,
  isLoadingProjectMediaDelete: false,
  story: [],
  policy: [],
  accreditation: [],
  leadership: [],
  error: null,
};

function normalizeExpertiseNode(expertise: Expertise): Expertise {
  return {
    ...expertise,
    children: Array.isArray(expertise.children)
      ? expertise.children.map((child) => normalizeExpertiseNode(child))
      : [],
  };
}

function buildTreeFromFlatExpertises(expertises: Expertise[]): Expertise[] {
  const nodeMap = new Map<number, Expertise>();

  expertises.forEach((expertise) => {
    nodeMap.set(expertise.id, {
      ...expertise,
      children: [],
    });
  });

  const roots: Expertise[] = [];

  nodeMap.forEach((expertise) => {
    const parentId = expertise.parentExpertiseId;
    if (parentId == null) {
      roots.push(expertise);
      return;
    }

    const parent = nodeMap.get(parentId);
    if (!parent) {
      roots.push(expertise);
      return;
    }

    parent.children = [...(parent.children || []), expertise];
  });

  return roots;
}

function normalizeExpertisesForState(expertises: any): Expertise[] {
  if (!Array.isArray(expertises)) {
    return [];
  }

  const normalized = expertises.map((item) => normalizeExpertiseNode(item));
  const hasTreeChildren = normalized.some((item) => !!item.children?.length);

  if (hasTreeChildren) {
    const allIds = new Set<number>(normalized.map((item) => item.id));
    const roots = normalized.filter((item) => {
      const parentId = item.parentExpertiseId;
      return parentId == null || !allIds.has(parentId);
    });

    return roots.length ? roots : normalized;
  }

  return buildTreeFromFlatExpertises(normalized);
}

function normalizeExpertiseHeroResponse(response: any): ExpertiseHeroSection | null {
  if (!response) {
    return null;
  }

  const candidate =
    response?.heroSection ??
    response?.hero ??
    response?.data?.heroSection ??
    response?.data?.hero ??
    response?.data ??
    response;

  if (!candidate || typeof candidate !== 'object') {
    return null;
  }

  const title = String(candidate.title ?? candidate.pageTitle ?? candidate.heading ?? '').trim();
  const subtitle = String(candidate.subtitle ?? candidate.subTitle ?? candidate.description ?? '').trim();
  const buttonText = String(candidate.buttonText ?? candidate.ctaText ?? candidate.ctaLabel ?? '').trim();
  const videoSrc = String(candidate.videoSrc ?? candidate.video ?? candidate.heroVideo ?? '').trim();
  const imageSrc = String(candidate.imageSrc ?? candidate.image ?? candidate.thumbnail ?? candidate.poster ?? '').trim();

  // Treat payloads with no visible media/copy as empty hero data.
  if (!title && !subtitle && !buttonText && !videoSrc && !imageSrc) {
    return null;
  }

  const rawVideoType = candidate.videoType ?? candidate.mediaType ?? candidate.type;
  const normalizedVideoType = (() => {
    if (typeof rawVideoType !== 'string') {
      return undefined;
    }

    const value = rawVideoType.toLowerCase();
    if (value.includes('youtube')) {
      return 'youtube' as const;
    }
    if (value.includes('local') || value.includes('video')) {
      return 'local' as const;
    }

    return undefined;
  })();

  return {
    id: Number(candidate.id ?? 0),
    title,
    subtitle,
    buttonText,
    videoSrc,
    videoType: normalizedVideoType ?? 'local',
    imageSrc,
  };
}

function parseExpertisesResponse(response: any): {
  expertises: Expertise[];
  heroSection: ExpertiseHeroSection | null;
  pageTitle: string | null;
  pageDescription: string | null;
} {
  const payload = response?.data ?? response ?? {};
  const expertises =
    payload?.expertises ??
    payload?.items ??
    response?.expertises ??
    response?.items ??
    (Array.isArray(payload) ? payload : []);

  const normalizedHeroSection = normalizeExpertiseHeroResponse(response);

  const heroCandidate =
    payload?.heroSection ??
    payload?.hero ??
    response?.heroSection ??
    response?.hero ??
    response?.data?.heroSection ??
    response?.data?.hero ??
    response?.data ??
    response ??
    null;

  const pageTitle =
    payload?.pageTitle ??
    payload?.title ??
    response?.pageTitle ??
    response?.title ??
    heroCandidate?.pageTitle ??
    null;

  const pageDescription =
    payload?.pageDescription ??
    payload?.pageSubTitle ??
    payload?.description ??
    response?.pageDescription ??
    response?.pageSubTitle ??
    response?.description ??
    heroCandidate?.pageDescription ??
    heroCandidate?.pageSubTitle ??
    null;

  return {
    expertises: normalizeExpertisesForState(expertises),
    heroSection: normalizedHeroSection,
    pageTitle,
    pageDescription,
  };
}

export const globalReducer = createReducer(
  initialState,
  // Fetch Profile
  on(GlobalActions.fetchProfile, (state) => ({
    ...state,
    loading: true,
    error: null,
  })),
  on(GlobalActions.fetchProfileSuccess, (state, { payload }) => ({
    ...state,
    profile: payload.data,
    loading: false,
    error: null,
  })),
  on(GlobalActions.fetchProfileError, (state, action) => ({
    ...state,
    loading: false,
    error: action.error,
  })),
  // Load Translation
  on(GlobalActions.loadTranslation, (state, { language }) => ({
    ...state,
    currentLanguage: language,
  })),
  on(GlobalActions.loadTranslationSuccess, (state, { data }) => ({
    ...state,
    translations: data,
  })),
  // Send Email
  on(GlobalActions.sendEmail, (state) => ({
    ...state,
    emailLoading: true,
  })),
  on(GlobalActions.sendEmailSuccess, (state) => ({
    ...state,
    emailLoading: false,
  })),
  on(GlobalActions.sendEmailError, (state) => ({
    ...state,
    emailLoading: false,
  })),
  // Send Resume
  on(GlobalActions.sendResume, (state) => ({
    ...state,
    emailLoading: true,
  })),
  on(GlobalActions.sendResumeSuccess, (state) => ({
    ...state,
    emailLoading: false,
  })),
  on(GlobalActions.sendResumeError, (state) => ({
    ...state,
    emailLoading: false,
  })),
  // Fetch Projects
  on(GlobalActions.fetchAllProjects, (state) => ({
    ...state,
    isLoading: true,
  })),
  on(GlobalActions.fetchAllProjectsSuccess, (state, { response }) => ({
    ...state,
    isLoading: false,
    projectsList: response,
  })),
  on(GlobalActions.fetchAllProjectsError, (state, action) => ({
    ...state,
    isLoading: false,
    error: action.error,
  })),
  // Fetch Expertises
  on(GlobalActions.fetchAllExpertises, (state) => ({
    ...state,
    isLoading: true,
  })),
  on(GlobalActions.fetchAllExpertisesSuccess, (state, { response }) => {
    const parsedExpertises = parseExpertisesResponse(response);
    return {
      ...state,
      isLoading: false,
      expertisesList: parsedExpertises.expertises,
      // Keep dedicated hero endpoint authoritative and avoid race-based overwrites.
      expertiseHeroSection: state.expertiseHeroSection ?? parsedExpertises.heroSection,
      expertisePageTitle: parsedExpertises.pageTitle,
      expertisePageDescription: parsedExpertises.pageDescription,
    };
  }),
  on(GlobalActions.fetchAllExpertisesError, (state, action) => ({
    ...state,
    isLoading: false,
    error: action.error,
  })),
  on(GlobalActions.fetchExpertiseHero, (state) => ({
    ...state,
    isLoading: true,
  })),
  on(GlobalActions.fetchExpertiseHeroSuccess, (state, { response }) => ({
    ...state,
    isLoading: false,
    expertiseHeroSection: normalizeExpertiseHeroResponse(response),
  })),
  on(GlobalActions.fetchExpertiseHeroError, (state, action) => ({
    ...state,
    isLoading: false,
    expertiseHeroSection: null,
    error: action.error,
  })),
  // Fetch Active Details
  on(GlobalActions.fetchActiveDetails, (state) => ({
    ...state,
    isLoading: true,
  })),
  on(GlobalActions.fetchActiveDetailsSuccess, (state, { response }) => ({
    ...state,
    isLoading: false,
    activeDetails: response,
  })),
  on(GlobalActions.fetchActiveDetailsError, (state, action) => ({
    ...state,
    isLoading: false,
    error: action.error,
  })),
  // Fetch Home Hero
  on(GlobalActions.fetchHomeHero, (state) => ({
    ...state,
    isLoading: true,
  })),
  on(GlobalActions.fetchHomeHeroSuccess, (state, { response }) => ({
    ...state,
    isLoading: false,
    homeHeroSection: response,
  })),
  on(GlobalActions.fetchHomeHeroError, (state, action) => ({
    ...state,
    isLoading: false,
    error: action.error,
  })),
  // Delete Project Media
  on(GlobalActions.deleteMedia, (state) => ({
    ...state,
    isLoadingProjectMediaDelete: true,
  })),
  on(GlobalActions.deleteMediaSuccess, (state) => ({
    ...state,
    isLoadingProjectMediaDelete: false,
  })),
  on(GlobalActions.deleteMediaError, (state, action) => ({
    ...state,
    isLoadingProjectMediaDelete: false,
    error: action.error,
  })),
  // Fetch All News
  on(GlobalActions.fetchAllNews, (state) => ({
    ...state,
    isLoading: true,
  })),
  on(GlobalActions.fetchAllNewsSuccess, (state, { response }) => ({
    ...state,
    isLoading: false,
    newsList: response,
  })),
  on(GlobalActions.fetchAllNewsError, (state, action) => ({
    ...state,
    isLoading: false,
    error: action.error,
  })),
  // Fetch All Careers
  on(GlobalActions.fetchAllCareers, (state) => ({
    ...state,
    isLoading: true,
  })),
  on(GlobalActions.fetchAllCareersSuccess, (state, { response }) => ({
    ...state,
    careers: response.careers || [],
    careerHeroSection: response.heroSection || null,
    isLoading: false,
  })),
  on(GlobalActions.fetchAllCareersError, (state, action) => ({
    ...state,
    error: action.err,
    isLoading: false,
  })),
  // Fetch Story
  on(GlobalActions.fetchStory, (state) => ({
    ...state,
    isLoading: true,
  })),
  on(GlobalActions.fetchStorySuccess, (state, { response }) => ({
    ...state,
    story: response[0],
    isLoading: false,
  })),
  on(GlobalActions.fetchStoryError, (state, action) => ({
    ...state,
    error: action.error,
    isLoading: false,
  })),
  // Fetch Leadership
  on(GlobalActions.fetchLeadership, (state) => ({
    ...state,
    isLoading: true,
  })),
  on(GlobalActions.fetchLeadershipSuccess, (state, { response }) => ({
    ...state,
    leadership: response[0],
    isLoading: false,
  })),
  on(GlobalActions.fetchLeadershipError, (state, action) => ({
    ...state,
    error: action.error,
    isLoading: false,
  })),
  // Fetch Policy
  on(GlobalActions.fetchPolicy, (state) => ({
    ...state,
    isLoading: true,
  })),
  on(GlobalActions.fetchPolicySuccess, (state, { response }) => ({
    ...state,
    policy: response[0],
    isLoading: false,
  })),
  on(GlobalActions.fetchPolicyError, (state, action) => ({
    ...state,
    error: action.error,
    isLoading: false,
  })),
  // Fetch Accreditation
  on(GlobalActions.fetchAccreditation, (state) => ({
    ...state,
    isLoading: true,
  })),
  on(GlobalActions.fetchAccreditationSuccess, (state, { response }) => ({
    ...state,
    accreditation: response[0],
    isLoading: false,
  })),
  on(GlobalActions.fetchAccreditationError, (state, action) => ({
    ...state,
    error: action.error,
    isLoading: false,
  })),
  // Delete Details
  on(GlobalActions.deleteDetails, (state) => ({
    ...state,
    isLoading: true,
  })),
  on(GlobalActions.deleteDetailsSuccess, (state) => ({
    ...state,
    isLoading: false,
  })),
  on(GlobalActions.deleteDetailsError, (state, { error }) => ({
    ...state,
    isLoading: false,
    error: error,
  })),
  // Fetch All Contacts
  on(GlobalActions.fetchContacts, (state) => ({
    ...state,
    isLoading: true,
  })),
  on(GlobalActions.fetchContactsSuccess, (state, { response }) => ({
    ...state,
    contactsList: response,
    isLoading: false,
  })),
  on(GlobalActions.fetchContactsError, (state, { error }) => ({
    ...state,
    isLoading: false,
    error: error,
  })),
  // Create Details
  on(GlobalActions.createDetails, (state) => ({
    ...state,
    isLoading: true,
  })),
  on(GlobalActions.createDetailsSuccess, (state) => ({
    ...state,
    isLoading: false,
  })),
  on(GlobalActions.createDetailsError, (state, action) => ({
    ...state,
    isLoading: false,
    error: action.error,
  })),
  on(GlobalActions.loadVideoSuccess, (state, { video }) => ({
    ...state,
    video,
    error: null,
  })),
  on(GlobalActions.loadVideoFailure, (state, { error }) => ({
    ...state,
    error,
  }))
);
